import { createTheme, MantineThemeProvider } from '@mantine/core';
import { useLayoutEffect } from 'react';

import { useAuth } from 'contexts/auth';

import './primaryColor.css';

import type { PropsWithChildren, ReactNode } from 'react';

const PRIMARY_COLOR_ATTRIBUTE = 'data-klue-primary-color';

// nectar's theme makes `cyan` the primary color, which is Hatch's brand
// color. Every other surface keeps `blue`. Button, ActionIcon, ThemeIcon and
// Tag set `color` through nectar's default props rather than reading
// `primaryColor`, so they need their own override.
const competeTheme = createTheme({
  primaryColor: 'blue',
  components: {
    ActionIcon: { defaultProps: { color: 'blue' } },
    Button: { defaultProps: { color: 'blue' } },
    Tag: { defaultProps: { color: 'blue' } },
    ThemeIcon: { defaultProps: { color: 'blue' } },
  },
});

/**
 * Sets Mantine's primary color for the session: `cyan` for Hatch-exclusive
 * accounts and `blue` for everyone else. Components that need the primary
 * color by name (for example a Badge, whose default color is `gray`) read it
 * from `useMantineTheme().primaryColor`.
 */
export function PrimaryColorProvider({
  children,
}: PropsWithChildren): ReactNode {
  const { isHatchExclusive } = useAuth();

  useLayoutEffect(() => {
    if (isHatchExclusive) return;

    const root = document.documentElement;
    root.setAttribute(PRIMARY_COLOR_ATTRIBUTE, 'blue');

    return () => root.removeAttribute(PRIMARY_COLOR_ATTRIBUTE);
  }, [isHatchExclusive]);

  if (isHatchExclusive) return children;

  return (
    <MantineThemeProvider theme={competeTheme}>{children}</MantineThemeProvider>
  );
}
